import React, { useState } from "react";
import { Languages, Loader2, CheckCircle2, AlertTriangle, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Outil de traduction batch des produits boutique (FR → DE + IT).
 * Utilise Claude Sonnet via Emergent LLM Key. Coût négligeable pour ~10-20 produits.
 *
 * Stratégie : ne re-traduit pas les produits déjà traduits (sauf force=true).
 * Le user fait ensuite le controlling manuellement sur chaque fiche produit.
 */
export default function TranslateProductsDialog() {
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);
  const [force, setForce] = useState(false);

  const start = async () => {
    setWorking(true);
    setResult(null);
    try {
      const { data } = await api.post(`/admin/translate-products?force=${force}&only_visible=true`);
      setResult(data);
      if (data.translated_count > 0) {
        toast.success(`${data.translated_count} produit(s) traduit(s)`);
      } else if (data.skipped_count > 0) {
        toast.info(`Tous les produits étaient déjà traduits (${data.skipped_count} skippés)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la traduction");
    } finally {
      setWorking(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); setForce(false); } }}>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE]"
        data-testid="open-translate-products"
      >
        <Languages className="w-4 h-4 mr-2" />
        Traduire la boutique (DE + IT)
      </Button>
      <AlertDialogContent className="max-w-2xl" data-testid="translate-products-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#1F4E3D]" />
            Traduire les produits boutique en allemand et italien
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-2">
            <span className="block">
              Utilise Claude Sonnet via Emergent LLM Key pour traduire automatiquement
              les champs : nom, descriptions, ingrédients, instructions de conservation
              et réchauffage. Les noms de plats thaï (Pad Thai, Sukiyaki…) restent inchangés.
            </span>
            <span className="block text-[#8C6B2F]">
              Vous pourrez ensuite faire le contrôle manuel produit par produit
              (vous parlez DE et IT, donc juste relecture).
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!working && !result && (
          <div className="space-y-3">
            <div className="border border-[#E5E5E0] rounded p-3 bg-[#FAFAF7] text-sm text-[#575A57]">
              Cible : produits visibles sur la boutique. Les déjà traduits ne sont pas re-traités
              (sauf si vous cochez "Forcer").
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                data-testid="translate-force-checkbox"
              />
              <span>Forcer la re-traduction (écrase les traductions existantes)</span>
            </label>
          </div>
        )}

        {working && (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-[#575A57]">
            <Loader2 className="w-8 h-8 animate-spin" />
            <div className="text-sm">Traduction en cours…</div>
            <div className="text-xs text-[#A3A6A3]">~2-3 secondes par produit</div>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="translate-products-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-[#C5DBCF] rounded p-3 bg-[#EAF4EE]">
                <div className="text-xs text-[#1F4E3D] uppercase tracking-wide">Traduits</div>
                <div className="text-xl font-mono font-medium text-[#1F4E3D]">{result.translated_count}</div>
              </div>
              <div className="border border-[#E5E5E0] rounded p-3 bg-[#FAFAF7]">
                <div className="text-xs text-[#575A57] uppercase tracking-wide">Skippés</div>
                <div className="text-xl font-mono font-medium text-[#575A57]">{result.skipped_count}</div>
              </div>
              <div className={`border rounded p-3 ${result.failed_count > 0 ? "border-[#C05C47] bg-[#FCEEEA]" : "border-[#E5E5E0] bg-[#FAFAF7]"}`}>
                <div className={`text-xs uppercase tracking-wide ${result.failed_count > 0 ? "text-[#C05C47]" : "text-[#A3A6A3]"}`}>Échoués</div>
                <div className={`text-xl font-mono font-medium ${result.failed_count > 0 ? "text-[#C05C47]" : "text-[#A3A6A3]"}`}>{result.failed_count}</div>
              </div>
            </div>

            {result.translated_count > 0 && (
              <div className="border border-[#C5DBCF] bg-[#EAF4EE] rounded p-3 text-xs text-[#1F4E3D]">
                <CheckCircle2 className="w-4 h-4 inline-block mr-1.5" />
                Traductions appliquées. Vérifiez les fiches produit avant de redéployer.
              </div>
            )}

            {result.details && result.details.length > 0 && (
              <div className="border border-[#E5E5E0] rounded max-h-60 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F4F4F0] sticky top-0">
                    <tr className="text-[#575A57]">
                      <th className="text-left p-2">Produit</th>
                      <th className="text-right p-2">Champs traduits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.details.map((d) => (
                      <tr key={d.product_id} className="border-t border-[#E5E5E0]">
                        <td className="p-2">{d.name}</td>
                        <td className="p-2 text-right font-mono">{d.fields_translated}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.failed && result.failed.length > 0 && (
              <div className="border border-[#E8C1B4] rounded p-3 bg-[#FCEEEA]/40">
                <div className="text-xs text-[#C05C47] font-medium mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Échecs (à retenter)
                </div>
                <ul className="text-[11px] text-[#7A3324] space-y-0.5">
                  {result.failed.map((f) => <li key={f.product_id}>{f.name}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="translate-cancel">Fermer</AlertDialogCancel>
          {!result && (
            <AlertDialogAction
              onClick={start}
              disabled={working}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="translate-start-btn"
            >
              {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Lancer la traduction
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
